/**
 * Support Guide: Set Up Divisions, Heats & Riders
 * Category: Management
 *
 * 720 frames @ 30fps = 24 seconds
 *
 * Timeline:
 *   0-55      Title card
 *   55-160    Empty setup, explain layout
 *   160-310   Cursor types + clicks Add for 3 divisions
 *   310-440   Cursor types heat names, selects division, clicks Add
 *   440-590   Cursor expands heat, clicks Add on riders one by one
 *   590-640   Show completed setup
 *   640-720   End scene
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { SlideIn } from "../../components/SlideIn";
import { TypeWriter } from "../../components/TypeWriter";
import { StepLabel } from "../../components/StepLabel";
import { AutoCursor } from "../../components/AutoCursor";

const ACCENT = "#a855f7";
const C = {
  bg: "#0a0a0b", surface: "#111113", surface2: "#1a1a1e",
  border: "#2a2a32", text: "#e4e4e7", textMuted: "#71717a", textDim: "#52525b",
  accent: "#3b82f6", danger: "#ef4444", success: "#22c55e", warning: "#eab308",
};
const F = { sans: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace" };

export function SetupCompetitionGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: C.bg, fontFamily: F.sans }}>
      <Sequence durationInFrames={55}>
        <TitleCard />
      </Sequence>
      <Sequence from={55} durationInFrames={785}>
        <Walkthrough />
      </Sequence>
    </AbsoluteFill>
  );
}

function TitleCard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: F.mono, marginBottom: 16 }}>
          Support Guide
        </div>
        <div style={{ fontSize: 48, fontWeight: 800, color: "#fff", maxWidth: 900, lineHeight: 1.1 }}>
          Set Up Divisions, Heats & Riders
        </div>
        <div style={{ marginTop: 20 }}>
          <TypeWriter text="Management" delay={10} speed={1} style={{ fontSize: 16, color: "#94a3b8" }} />
        </div>
      </div>
    </AbsoluteFill>
  );
}

function typeText(text: string, startFrame: number, currentFrame: number, charsPerFrame = 0.4): string {
  if (currentFrame < startFrame) return "";
  const chars = Math.floor((currentFrame - startFrame) * charsPerFrame);
  return text.slice(0, Math.min(chars, text.length));
}

const ALL_RIDERS = [
  { name: "Harley Clifford", nick: "The King", initials: "HC", colour: C.accent, level: "Pro" },
  { name: "Nic Rapa", nick: "Raptor", initials: "NR", colour: C.success, level: "Pro" },
  { name: "Dallas Friday", nick: "D-Fri", initials: "DF", colour: C.warning, level: "Pro" },
  { name: "Amber Wing", nick: "Wings", initials: "AW", colour: C.danger, level: "Open" },
  { name: "Steel Lafferty", nick: "Steele", initials: "SL", colour: "#a855f7", level: "Pro" },
];

function Walkthrough() {
  const frame = useCurrentFrame();

  // ── Division: type + add ──
  // Jnr: type 160-185, cursor→Add 190, click 195
  // Women's: type 210-245, cursor→Add 250, click 255
  // Pro: type 270-290, cursor→Add 295, click 300
  const divTypingJnr = typeText("Jnr", 160, frame);
  const divTypingWomens = typeText("Women's", 210, frame);
  const divTypingPro = typeText("Pro", 270, frame);

  const divisions: string[] = [];
  if (frame >= 195) divisions.push("Jnr");
  if (frame >= 255) divisions.push("Women's");
  if (frame >= 300) divisions.push("Pro");

  const divInputText = frame >= 160 && frame < 195 ? divTypingJnr
    : frame >= 210 && frame < 255 ? divTypingWomens
    : frame >= 270 && frame < 300 ? divTypingPro
    : "";

  // ── Heat: type + open dropdown + select division + click Add ──
  // Slower pacing: ~80 frames per heat, dropdown open for 25 frames
  // Heat 1 for Jnr: type 310-350, dropdown 360-385, select 385, add 400
  // Heat 1 for Women's: type 415-455, dropdown 465-490, select 490, add 505
  // Heat 1 for Pro: type 520-560, dropdown 570-595, select 595, add 610
  const heatTyping1 = typeText("Heat 1", 310, frame, 0.25);
  const heatTyping2 = typeText("Heat 1", 415, frame, 0.25);
  const heatTyping3 = typeText("Heat 1", 520, frame, 0.25);

  type HeatDef = { name: string; division: string; maxAttempts: number };
  const heats: HeatDef[] = [];
  if (frame >= 400) heats.push({ name: "Heat 1", division: "Jnr", maxAttempts: 3 });
  if (frame >= 505) heats.push({ name: "Heat 1", division: "Women's", maxAttempts: 3 });
  if (frame >= 610) heats.push({ name: "Heat 1", division: "Pro", maxAttempts: 3 });

  const heatInputText = frame >= 310 && frame < 400 ? heatTyping1
    : frame >= 415 && frame < 505 ? heatTyping2
    : frame >= 520 && frame < 610 ? heatTyping3
    : "";

  // Division dropdown: open for 25 frames so it's clearly visible
  const dropdownOpen = (frame >= 360 && frame < 385) || (frame >= 465 && frame < 490) || (frame >= 570 && frame < 595);
  const selectedDiv = frame >= 595 ? "Pro" : frame >= 490 ? "Women's" : frame >= 385 ? "Jnr" : "";

  // ── Expand Pro Heat 1 + add riders ──
  const expandedHeat = frame >= 630 ? "Pro" : null;

  const addedRiders: typeof ALL_RIDERS = [];
  if (frame >= 670) addedRiders.push(ALL_RIDERS[0]); // Harley
  if (frame >= 710) addedRiders.push(ALL_RIDERS[1]); // Nic
  if (frame >= 750) addedRiders.push(ALL_RIDERS[2]); // Dallas

  const addedNames = new Set(addedRiders.map((r) => r.name));
  const availableRiders = ALL_RIDERS.filter((r) => !addedNames.has(r.name));

  const showEnd = frame >= 790;
  const endOpacity = showEnd ? interpolate(frame, [790, 810], [0, 1], { extrapolateRight: "clamp" }) : 0;
  const mainOpacity = showEnd ? 0 : 1;

  // ── Cursor points (slower, more travel time between actions) ──
  const cursorPoints = [
    { x: 960, y: 400, frame: 0 },
    // Click in division input, type Jnr
    { target: "div-input", frame: 150 },
    { target: "div-input", frame: 160, click: true },
    // Click Add for Jnr
    { target: "div-add-btn", frame: 188 },
    { target: "div-add-btn", frame: 195, click: true },
    // Type Women's, click Add
    { target: "div-input", frame: 205 },
    { target: "div-add-btn", frame: 248 },
    { target: "div-add-btn", frame: 255, click: true },
    // Type Pro, click Add
    { target: "div-input", frame: 265 },
    { target: "div-add-btn", frame: 293 },
    { target: "div-add-btn", frame: 300, click: true },

    // ── Heat 1 for Jnr ──
    { target: "heat-input", frame: 305 },
    { target: "heat-input", frame: 310, click: true },
    // Move to dropdown, click to open
    { target: "heat-div-select", frame: 355 },
    { target: "heat-div-select", frame: 360, click: true },
    // Hover over Jnr option, click to select
    { target: "dropdown-jnr", frame: 375 },
    { target: "dropdown-jnr", frame: 385, click: true },
    // Click Add
    { target: "heat-add-btn", frame: 393 },
    { target: "heat-add-btn", frame: 400, click: true },

    // ── Heat 1 for Women's ──
    { target: "heat-input", frame: 410 },
    { target: "heat-input", frame: 415, click: true },
    { target: "heat-div-select", frame: 458 },
    { target: "heat-div-select", frame: 465, click: true },
    { target: "dropdown-womens", frame: 480 },
    { target: "dropdown-womens", frame: 490, click: true },
    { target: "heat-add-btn", frame: 498 },
    { target: "heat-add-btn", frame: 505, click: true },

    // ── Heat 1 for Pro ──
    { target: "heat-input", frame: 515 },
    { target: "heat-input", frame: 520, click: true },
    { target: "heat-div-select", frame: 563 },
    { target: "heat-div-select", frame: 570, click: true },
    { target: "dropdown-pro", frame: 585 },
    { target: "dropdown-pro", frame: 595, click: true },
    { target: "heat-add-btn", frame: 603 },
    { target: "heat-add-btn", frame: 610, click: true },

    // ── Expand Pro Heat 1 ──
    { target: "pro-heat1", frame: 622 },
    { target: "pro-heat1", frame: 630, click: true },
    // Add Harley
    { target: "add-rider-0", frame: 658 },
    { target: "add-rider-0", frame: 670, click: true },
    // Add Nic
    { target: "add-rider-1", frame: 698 },
    { target: "add-rider-1", frame: 710, click: true },
    // Add Dallas
    { target: "add-rider-2", frame: 738 },
    { target: "add-rider-2", frame: 750, click: true },
    // Rest
    { x: 400, y: 400, frame: 780 },
  ];

  return (
    <AbsoluteFill>
      <AbsoluteFill style={{ opacity: mainOpacity, position: "relative" }}>
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <nav style={{ display: "flex", gap: 16 }}>
              <span style={{ ...navLink, color: C.text }}>Competitions</span>
              <span style={navLink}>Riders</span>
              <span style={navLink}>Training</span>
            </nav>
            <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>admin@koda-software.com</span>
          </div>

          <div style={{ padding: 24, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div>
                <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Setup: Boardstock</h1>
                <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>Configure divisions, heats, assign riders, invite judges</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* ── Left: Divisions & Heats ── */}
              <div>
                {/* Divisions */}
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.5, marginBottom: 12 }}>Divisions</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <div id="div-input" style={{ ...inputField, flex: 1, display: "flex", alignItems: "center" }}>
                      {divInputText ? (
                        <span>{divInputText}<span style={{ opacity: frame % 20 < 10 ? 1 : 0, color: C.accent }}>|</span></span>
                      ) : (
                        <span style={{ color: C.textMuted }}>Division name (e.g. Men)</span>
                      )}
                    </div>
                    <span id="div-add-btn" style={btnPrimary}>Add</span>
                  </div>
                  {divisions.length === 0 && (
                    <div style={{ fontSize: 12, color: C.textDim, textAlign: "center" as const, padding: "12px 0" }}>No divisions yet</div>
                  )}
                  {divisions.map((d) => (
                    <div key={d} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{d}</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <span style={{ fontSize: 10, color: C.textMuted, fontWeight: 600, textTransform: "uppercase" as const }}>Edit</span>
                        <span style={{ fontSize: 10, color: C.danger, fontWeight: 600, textTransform: "uppercase" as const, padding: "2px 6px", border: `1px solid ${C.danger}` }}>Delete</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Heats */}
                <div style={card}>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.5, marginBottom: 12 }}>Heats</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div id="heat-input" style={{ ...inputField, flex: 1, display: "flex", alignItems: "center" }}>
                      {heatInputText ? (
                        <span>{heatInputText}<span style={{ opacity: frame % 20 < 10 ? 1 : 0, color: C.accent }}>|</span></span>
                      ) : (
                        <span style={{ color: C.textMuted }}>Heat name...</span>
                      )}
                    </div>
                    <span id="heat-add-btn" style={btnPrimary}>Add</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={fieldLabel}>Division</div>
                      <div style={{ position: "relative" }}>
                        <div id="heat-div-select" style={{ ...inputField, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {selectedDiv ? (
                            <span>{selectedDiv}</span>
                          ) : (
                            <span style={{ color: C.textMuted }}>Select a division...</span>
                          )}
                          <span style={{ fontSize: 8, color: C.textMuted }}>▼</span>
                        </div>
                        {dropdownOpen && divisions.length > 0 && (
                          <div style={{
                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
                            background: C.surface2, border: `1px solid ${C.border}`, borderTop: "none",
                          }}>
                            {divisions.map((d) => (
                              <div
                                key={d}
                                id={`dropdown-${d.toLowerCase().replace("'", "")}`}
                                style={{ padding: "6px 12px", fontSize: 12, color: C.text, borderBottom: `1px solid ${C.border}` }}
                              >
                                {d}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={fieldLabel}>Max Attempts</div>
                      <div style={{ display: "flex", gap: 3 }}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <div key={n} style={{
                            flex: "1 1 0", minWidth: 0, padding: "4px 0", textAlign: "center" as const,
                            fontSize: 10, fontWeight: 600,
                            background: n === 3 ? C.accent : C.surface2,
                            color: n === 3 ? "#fff" : C.text,
                            border: `1px solid ${n === 3 ? C.accent : C.border}`,
                          }}>{n}</div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Heats grouped by division */}
                  {heats.length === 0 && divisions.length > 0 && (
                    <div style={{ fontSize: 12, color: C.textDim, textAlign: "center" as const, padding: "12px 0" }}>No heats yet</div>
                  )}
                  {divisions.filter((d) => heats.some((h) => h.division === d)).map((d) => (
                    <div key={d} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 4, marginTop: 8 }}>{d}</div>
                      {heats.filter((h) => h.division === d).map((h) => (
                        <div key={`${d}-${h.name}`}>
                          <div
                            id={d === "Pro" && h.name === "Heat 1" ? "pro-heat1" : undefined}
                            style={{
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                              padding: "6px 8px", background: C.surface2, border: `1px solid ${C.border}`, marginBottom: 2,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 10, color: C.textDim }}>{expandedHeat === d && h.name === "Heat 1" ? "▼" : "▶"}</span>
                              <span style={{ fontWeight: 700, fontSize: 12 }}>{h.name}</span>
                            </div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span style={badgeOff}>Max: 10</span>
                              <span style={badgeOff}>{h.maxAttempts} attempts</span>
                              <span style={{ fontSize: 9, color: C.danger, fontWeight: 600, textTransform: "uppercase" as const, padding: "1px 5px", border: `1px solid ${C.danger}` }}>Delete</span>
                            </div>
                          </div>

                          {/* Expanded: rider picker */}
                          {expandedHeat === d && h.name === "Heat 1" && (
                            <div style={{ padding: "8px 12px", border: `1px solid ${C.border}`, borderTop: "none", marginBottom: 4 }}>
                              {addedRiders.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Riders in heat:</div>
                                  {addedRiders.map((r) => (
                                    <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: r.colour, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{r.initials}</div>
                                      <span style={{ fontSize: 12, fontWeight: 600 }}>{r.name}</span>
                                      <span style={{ fontSize: 10, color: C.textMuted }}>&ldquo;{r.nick}&rdquo;</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Add rider:</div>
                              {availableRiders.map((r, i) => (
                                <div key={r.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: r.colour, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{r.initials}</div>
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: 600 }}>{r.name}</div>
                                      <div style={{ fontSize: 10, color: C.textMuted }}>&ldquo;{r.nick}&rdquo; <span style={{ color: C.textDim }}>{r.level}</span></div>
                                    </div>
                                  </div>
                                  <span id={`add-rider-${i}`} style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, color: C.textMuted }}>Add</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Right: Settings & Invite ── */}
              <div>
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.5, marginBottom: 12 }}>Competition Settings</div>
                  <div style={fieldLabel}>Scoring Model</div>
                  <div style={{ ...inputField, marginBottom: 8, marginTop: 4 }}>Best Score (Max)</div>
                  <div style={{ fontSize: 11, color: C.textDim, marginBottom: 12 }}>Rider keeps only their highest judge score</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>Judges: 3 (set at creation, cannot be changed)</div>

                  <div style={{ ...fieldLabel, marginTop: 16, marginBottom: 8 }}>Overlay Defaults (Applied on Go Live)</div>
                  {["Show heat name banner", "Show heat riders", "Show rider scores", "Show leaderboard"].map((opt, i) => (
                    <div key={opt} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}>
                      <div style={{ width: 14, height: 14, border: `1px solid ${i < 2 ? C.accent : C.border}`, background: i < 2 ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {i < 2 && <span style={{ color: "#fff", fontSize: 8, fontWeight: 700 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12, color: C.text }}>{opt}</span>
                    </div>
                  ))}

                  <div style={{ ...fieldLabel, marginTop: 16, marginBottom: 6 }}>Default Overlay Style</div>
                  <div style={inputField}>Steel</div>
                </div>

                <div style={card}>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.5, marginBottom: 12 }}>Invite Team</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ ...inputField, flex: 1 }}><span style={{ color: C.textMuted }}>Email address</span></div>
                    <span style={btnPrimary}>Invite</span>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    {["Judge", "Producer", "Admin"].map((role, i) => (
                      <div key={role} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ width: 14, height: 14, border: `1px solid ${i === 0 ? C.accent : C.border}`, background: i === 0 ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {i === 0 && <span style={{ color: "#fff", fontSize: 8, fontWeight: 700 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: C.text }}>{role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── AutoCursor ── */}
        {!showEnd && <AutoCursor points={cursorPoints} />}
      </AbsoluteFill>

      {/* Step labels */}
      <StepLabel text="The Setup page — divisions, heats, riders, settings, invites" show={0} hide={110} color={ACCENT} />
      <StepLabel text="Type a division name and click Add" show={110} hide={200} color={ACCENT} />
      <StepLabel text="Add more divisions — Jnr, Women's, Pro" show={200} hide={310} color={ACCENT} />
      <StepLabel text="Type a heat name, open the dropdown to pick a division, click Add" show={310} hide={620} color={ACCENT} />
      <StepLabel text="Expand a heat — click Add next to each rider to assign them" show={620} hide={770} color={ACCENT} />
      <StepLabel text="Riders are assigned — your competition is ready" show={770} hide={790} color={ACCENT} />

      {/* End scene */}
      {showEnd && (
        <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: endOpacity }}>
          <SlideIn>
            <div style={{ textAlign: "center", maxWidth: 600 }}>
              <div style={{ fontSize: 42, fontWeight: 800, color: ACCENT, marginBottom: 20 }}>
                Competition ready
              </div>
              <div style={{ fontSize: 18, color: "#94a3b8", lineHeight: 1.6, marginBottom: 28 }}>
                Divisions, heats, and riders are all set.
                <br />
                Head to Producer to go live.
              </div>
              <div style={{ display: "flex", gap: 24, justifyContent: "center" }}>
                {[
                  { label: "Divisions", desc: "Organise by category", color: ACCENT },
                  { label: "Heats", desc: "Rounds within divisions", color: C.accent },
                  { label: "Riders", desc: "Assigned per heat", color: C.success },
                ].map((item) => (
                  <div key={item.label} style={{ textAlign: "center", minWidth: 140 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: item.color, textTransform: "uppercase" as const, letterSpacing: 1, fontFamily: F.mono, marginBottom: 6 }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: C.textDim }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </SlideIn>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
}

const shell: React.CSSProperties = { width: "100%", height: "100%", background: C.bg, fontFamily: F.sans, color: C.text, display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: F.mono, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted };
const card: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, padding: 20 };
const inputField: React.CSSProperties = { padding: "8px 12px", background: C.surface, border: `1px solid ${C.border}`, fontSize: 13, color: C.text };
const fieldLabel: React.CSSProperties = { fontFamily: F.mono, fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted, marginBottom: 4 };
const btnPrimary: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "8px 16px", background: C.accent, color: "#fff", border: `1px solid ${C.accent}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 };
const badgeOff: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, background: "#222228", color: C.textMuted, fontFamily: F.mono };
